/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-study-treatment-arms/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';

import { useAuthorization, useBoolean, useSysAdminCanEditStudy, useTranslation } from 'hooks';
import { GET_STUDY_TREATMENT_ARMS } from 'queries/study-treatment-arms';
import NotificationService from 'utils/notification';
import AddTreatmentDialog from 'views/components/admin/add-treatment-arms-dialog';
import AdminStudyTreatmentArmsGrid from 'views/components/admin/admin-study-treatment-arms-grid';
import Loading from 'views/components/core/loading';
import Constants from '@utils/constants';

interface AdminStudyTreatmentArmsInterface {
    studyId: string;
    canEdit: boolean;
}

const AdminStudyTreatmentArms = ({ studyId, canEdit }: AdminStudyTreatmentArmsInterface) => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const addTreatmentDialog = useBoolean(false);
    const [treatmentArms, setTreatmentsArms] = useState([]);
    const [treatmentArmsData, setTreatmentArmsData] = useState([]);
    const [currentTreatment, setCurrentTreatment] = useState(null);
    const { loading, data } = useQuery(GET_STUDY_TREATMENT_ARMS, { variables: { studyId } });

    const isSysAdmin = user.isSystemAdmin(Constants.SystemAdminLevel.SysAdmin);
    const sysAdminCanEditStudy = useSysAdminCanEditStudy();
    useEffect(() => {
        if (data?.treatmentArms) {
            setTreatmentArmsData(data?.treatmentArms);
        }
    }, [data]);

    if (!user || !user.systemAdminLevel) return null;

    if (!studyId || loading) return <Loading />;

    return (
        <>
            <AddTreatmentDialog
                isSysAdmin={isSysAdmin}
                currentTreatment={currentTreatment}
                treatments={treatmentArms}
                open={addTreatmentDialog.value}
                onClose={(action: string | boolean | undefined) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'success')
                            NotificationService.showNotification(t('admin:successfulCreateTreatment'));
                        if (action === 'edit')
                            NotificationService.showNotification(
                                t('admin:successfulEditTreatment', { id: currentTreatment.recordId }),
                            );
                        addTreatmentDialog.setFalse();
                        setCurrentTreatment(null);
                    }
                }}
            />
            <AdminStudyTreatmentArmsGrid
                canEdit={canEdit}
                sysAdminCanEditStudy={sysAdminCanEditStudy}
                treatmentArmsData={treatmentArmsData}
                treatmentArms={treatmentArms}
                addTreatmentDialog={addTreatmentDialog}
                setCurrentTreatment={setCurrentTreatment}
                setTreatmentsArms={setTreatmentsArms}
            />
        </>
    );
};

export default AdminStudyTreatmentArms;
